import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  GET_AI_AUDIT_FILTER_OPTIONS,
  GET_AI_AUDITS,
} from '#src/graphql/queries/ai-audit';

export function AiAuditListPage() {
  const [endpointFilter, setEndpointFilter] = React.useState('');
  const [contextFilter, setContextFilter] = React.useState('');

  const { data: optionsData } = useQuery(GET_AI_AUDIT_FILTER_OPTIONS);
  const { data, loading, error, refetch } = useQuery(GET_AI_AUDITS, {
    variables: {
      limit: 200,
      endpoint: endpointFilter || null,
      context: contextFilter || null,
    },
  });

  const rows = data?.aiAudits ?? [];
  const endpoints = optionsData?.aiAuditFilterOptions.endpoints ?? [];
  const contexts = optionsData?.aiAuditFilterOptions.contexts ?? [];

  if (loading && !data) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          AI audit log
        </Heading>
        <Text>Loading…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={6}>
          AI audit log
        </Heading>
        <Text color="red.500">Error loading audit log: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          AI audit log
        </Heading>
        <HStack flexWrap="wrap" gap={4} align="flex-end">
          <Box maxW="280px">
            <Text fontSize="sm" color="fg.muted" mb={1}>
              Endpoint
            </Text>
            <select
              value={endpointFilter}
              onChange={(e) => setEndpointFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '6px',
                borderWidth: '1px',
              }}
            >
              <option value="">All</option>
              {endpoints.map((ep) => (
                <option key={ep} value={ep}>
                  {ep}
                </option>
              ))}
            </select>
          </Box>
          <Box maxW="280px">
            <Text fontSize="sm" color="fg.muted" mb={1}>
              Context
            </Text>
            <select
              value={contextFilter}
              onChange={(e) => setContextFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '6px',
                borderWidth: '1px',
              }}
            >
              <option value="">All</option>
              {contexts.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Box>
          <Button size="sm" onClick={() => refetch()}>
            Refresh
          </Button>
        </HStack>
        {rows.length === 0 ? (
          <Text color="fg.muted">No audit rows match the filters.</Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
                  <Table.ColumnHeader>Context</Table.ColumnHeader>
                  <Table.ColumnHeader>Event id</Table.ColumnHeader>
                  <Table.ColumnHeader>Description</Table.ColumnHeader>
                  <Table.ColumnHeader>Id</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell whiteSpace="nowrap">
                      {new Date(row.createdAt).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="xs">
                      {row.endpoint}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="xs">
                      {row.context ?? '—'}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="xs">
                      {row.eventId ?? '—'}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="xs">
                      {row.description ?? '—'}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="sm">
                      {row.id}
                    </Table.Cell>
                    <Table.Cell textAlign="end">
                      <Link asChild color="blue.600">
                        <RouterLink to={`/ai-audit/${row.id}`}>View</RouterLink>
                      </Link>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
